import type { z } from 'zod';
import type { PermissionKey } from '../permissions/index.js';
import { type CommandEnvelope, commandEnvelope } from './envelope.js';

/** Everything the server and stations need to know about one command type. */
export interface CommandDefinition<
  TType extends string = string,
  TPayload extends z.ZodType = z.ZodType,
> {
  /** Dotted type, e.g. `production.complete`. */
  type: TType;
  /** Payload schema versions accepted: the current one and, optionally, the previous. */
  versions: { current: number; previous?: number };
  /** Schema of the current payload version. */
  payload: TPayload;
  /** Schema of the previous version, if it differs (default: same as `payload`). */
  previousPayload?: z.ZodType;
  /** Maps a parsed previous-version payload to the current shape (re-validated after). */
  upgrade?: (previous: unknown) => unknown;
  /**
   * A physical fact (a count, a completed batch, a delivery). Physical commands are never
   * rejected for business reasons; they are applied with flags (plan §9.3).
   */
  physical: boolean;
  /** Permission needed, or `null` for none. */
  permission: PermissionKey | null;
}

/** Any command definition. */
// The payload type is erased here on purpose; `isCommand` narrows it back.
export type AnyCommandDefinition = CommandDefinition<string, z.ZodType>;

/** Defines a command. Identity function that checks the definition and keeps its types. */
export function defineCommand<const TType extends string, TPayload extends z.ZodType>(
  definition: CommandDefinition<TType, TPayload>,
): CommandDefinition<TType, TPayload> {
  assertDefinition(definition);
  return definition;
}

function assertDefinition(def: AnyCommandDefinition): void {
  if (!commandEnvelope.shape.type.safeParse(def.type).success) {
    throw new RangeError(`Bad command type: ${def.type}`);
  }
  const { current, previous } = def.versions;
  if (!Number.isSafeInteger(current) || current < 1) {
    throw new RangeError(`${def.type}: current version must be an integer >= 1`);
  }
  if (
    previous !== undefined &&
    (!Number.isSafeInteger(previous) || previous < 1 || previous >= current)
  ) {
    throw new RangeError(`${def.type}: previous version must be an integer in [1, current)`);
  }
}

/** A command whose envelope and payload passed validation. */
export interface ValidatedCommand<D extends AnyCommandDefinition = AnyCommandDefinition> {
  envelope: CommandEnvelope;
  definition: D;
  /** The payload, parsed and (if it was the previous version) upgraded to current. */
  payload: z.output<D['payload']>;
}

/** A validation problem, serialisable for `command_log`. */
export interface CommandIssue {
  /** Dotted path into the envelope, e.g. `payload.qty`. */
  path: string;
  message: string;
}

/** Why validation failed. */
export type CommandRejectReason =
  'invalid_envelope' | 'unknown_type' | 'unsupported_version' | 'invalid_payload';

/** Result of {@link CommandRegistry.validate}. */
export type CommandValidation =
  | { ok: true; command: ValidatedCommand }
  | { ok: false; reason: CommandRejectReason; issues: CommandIssue[] };

function toIssues(error: z.ZodError, prefix: string[] = []): CommandIssue[] {
  return error.issues.map((i) => ({
    path: [...prefix, ...i.path.map(String)].join('.'),
    message: i.message,
  }));
}

/** The set of commands a server or station understands. */
export class CommandRegistry {
  readonly #defs = new Map<string, AnyCommandDefinition>();

  constructor(definitions: readonly AnyCommandDefinition[]) {
    for (const def of definitions) {
      assertDefinition(def);
      if (this.#defs.has(def.type)) throw new Error(`Duplicate command type: ${def.type}`);
      this.#defs.set(def.type, def);
    }
  }

  /** The definition for `type`, if registered. */
  get(type: string): AnyCommandDefinition | undefined {
    return this.#defs.get(type);
  }

  /** All registered types, sorted. */
  types(): string[] {
    return [...this.#defs.keys()].sort();
  }

  /**
   * Validates a raw envelope: envelope shape → known type → supported version → payload.
   * A previous-version payload is parsed with the previous schema and upgraded.
   */
  validate(raw: unknown): CommandValidation {
    const env = commandEnvelope.safeParse(raw);
    if (!env.success) return { ok: false, reason: 'invalid_envelope', issues: toIssues(env.error) };
    const envelope = env.data;
    const def = this.#defs.get(envelope.type);
    if (!def) {
      return {
        ok: false,
        reason: 'unknown_type',
        issues: [{ path: 'type', message: `Unknown command type ${envelope.type}` }],
      };
    }
    const { current, previous } = def.versions;
    const version = envelope.schema_version;
    if (version !== current && version !== previous) {
      return {
        ok: false,
        reason: 'unsupported_version',
        issues: [
          {
            path: 'schema_version',
            message: `${def.type} accepts version ${current}${previous ? ` or ${previous}` : ''}, got ${version}`,
          },
        ],
      };
    }
    let input: unknown = envelope.payload;
    if (version !== current) {
      const prev = (def.previousPayload ?? def.payload).safeParse(input);
      if (!prev.success) {
        return { ok: false, reason: 'invalid_payload', issues: toIssues(prev.error, ['payload']) };
      }
      if (def.upgrade) {
        // An upgrade that throws means the old payload can't be expressed in the current
        // shape: that is an invalid payload, never an exception for the caller.
        try {
          input = def.upgrade(prev.data);
        } catch (err) {
          const detail = err instanceof Error ? err.message : String(err);
          return {
            ok: false,
            reason: 'invalid_payload',
            issues: [
              { path: 'payload', message: `cannot upgrade from version ${version}: ${detail}` },
            ],
          };
        }
      } else {
        input = prev.data;
      }
    }
    const payload = def.payload.safeParse(input);
    if (!payload.success) {
      return { ok: false, reason: 'invalid_payload', issues: toIssues(payload.error, ['payload']) };
    }
    return { ok: true, command: { envelope, definition: def, payload: payload.data } };
  }
}

/** Narrows a validated command to a specific definition (typed payload). */
export function isCommand<D extends AnyCommandDefinition>(
  command: ValidatedCommand,
  definition: D,
): command is ValidatedCommand<D> {
  return command.definition === definition;
}
